import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { apiEditMember } from "../../api/public";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import SimpleBox from "../../components/layout/container/SimpleBox";
import ShadowBox from "../../components/layout/container/ShadowBox";
import DialogSubmit from "../../components/dialog/DialogSubmit";
import SendButton from "../../components/input/button/SendButton";
import BackButton from "../../components/input/button/BackButton";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Title from "../../components/typography/Title";
import TextFormInput from "../../components/input/text/TextFormInput";
import TextFormDate from "../../components/input/text/TextFormDate";
import SelectFormInput from "../../components/input/select/SelectFormInput";
import { cardTypeMap, genderMap, maritalStatusMap } from "../../helpers/mapping";
import DialogSeasonConfirmation from "../dialog/DialogSeasonConfirmation";
import ReCAPTCHA from "react-google-recaptcha";

const PersonForm = ({ paramId, paramTopic }) => {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const recaptchaRef = useRef(null);
    const recaptchaKey = "6LdoA_gpAAAAACn8foSkx8VqrEh5C05uIpZc_B8b";

    const people = useSelector(state => state.user.people);
    const person = Boolean(paramId) ? people.find(element => element.id === paramId) : null;

    const [open, setOpen] = React.useState(false);
    const [openExternal, setOpenExternal] = React.useState(false);
    const [recaptcha, setRecaptcha] = React.useState(null);

    const formik = useFormik({
        initialValues: {
            fiscalNumber: null,
            shortName: null,
            fullName: null,
            email: null,
            paymentEmail: null,
            phoneNrMobilePrimary: null,
            phoneNrMobileSecondary: null,
            birthDate: null,
            address: null,
            town: null,
            postCode: null,
            idCardType: null,
            identificationNumber: null,
            idCardIssuedBy: null,
            idCardIssuedAt: null,
            idCardValidity: null,
            placeOfBirth: null,
            nationality: null,
            job: null,
            maritalStatus: null,
            gender: null,
            fatherName: null,
            motherName: null,
        },
        validationSchema: Yup.object().shape({
            fiscalNumber: Yup.string().required("O número fiscal é obrigatório"),
            shortName: Yup.string().required("O nome curto é obrigatório"),
            fullName: Yup.string().required("O nome completo é obrigatório"),
            email: Yup.string().email("Email inválido").nullable(),
            paymentEmail: Yup.string().email("Email inválido").required("O email de pagamento é obrigatório"),
            phoneNrMobilePrimary: Yup.string().required("O contacto principal é obrigatório"),
            phoneNrMobileSecondary: Yup.string().nullable(),
            birthDate: Yup.date("A data não é válida").required("A data de nascimento é obrigatória"),
            address: Yup.string().required("A morada é obrigatória"),
            town: Yup.string().required("A localidade é obrigatória"),
            postCode: Yup.string().required("O código postal é obrigatório"),
            idCardType: Yup.string().required("O tipo de documento é obrigatório"),
            identificationNumber: Yup.string().required("O número de identificação é obrigatório"),
            idCardIssuedBy: Yup.string().nullable(),
            idCardIssuedAt: Yup.date("A data não é válida").nullable(),
            idCardValidity: Yup.date("A data não é válida").required("A validade do cartão é obrigatória"),
            placeOfBirth: Yup.string().required("A naturalidade é obrigatória"),
            nationality: Yup.string().required("A nacionalidade é obrigatória"),
            job: Yup.string().nullable(),
            maritalStatus: Yup.string().nullable(),
            gender: Yup.string().required("O género é obrigatório"),
            fatherName: Yup.string().nullable(),
            motherName: Yup.string().nullable(),
        }),
        onSubmit: (values) => {
            if (paramTopic === "EXTERNAL") {
                setOpenExternal(true);
            }
            else {
                setOpen(true);
            }
        },
    });

    const handleSumbit = (action) => {
        if (action === "submit") {
            const recaptchaVal = paramTopic === "EXTERNAL" ? { recaptcha } : {};
            apiEditMember( { topic: paramTopic, personId: paramId === "null" ? null : paramId, ...prepareDataToSubmit(), ...recaptchaVal }, (data, successFlag) => {
                if (successFlag > 0) {
                    toast.success("Obrigado pela submissão. O seu registo foi enviado para validação.", { autoClose: 8000 });
                    if (paramTopic === "EXTERNAL") {
                        setOpenExternal(false);
                        navigate("/external-submit");
                    }
                    else {
                        setOpen(false);
                        navigate("/membership");
                    }
                }
                else {
                    setOpen(false);
                    setOpenExternal(false);
                }
            });
        }
        else {
            setOpen(false);
            setOpenExternal(false);
        }
    }

    const prepareDataToSubmit = () => {
        return {
            ...formik.values,
            birthDate: formik.values.birthDate ? formik.values.birthDate.format("YYYY-MM-DD") : null,
            idCardIssuedAt: formik.values.idCardIssuedAt ? formik.values.idCardIssuedAt.format("YYYY-MM-DD") : null,
            idCardValidity: formik.values.idCardValidity ? formik.values.idCardValidity.format("YYYY-MM-DD") : null,
        }
    }

    useEffect(() => {
        if (Boolean(person)) {
            formik.setValues({
                ...formik.values,
                fiscalNumber: person.fiscalNumber,
                shortName: person.shortName,
                fullName: person.fullName,
                email: person.email,
                paymentEmail: person.paymentEmail,
                phoneNrMobilePrimary: person.phoneNrMobilePrimary,
                phoneNrMobileSecondary: person.phoneNrMobileSecondary,
                birthDate: person.birthDate ? dayjs(person.birthDate) : null,
                address: person.address,
                town: person.town,
                postCode: person.postCode,
                idCardType: person.idCardType ? person.idCardType : "",
                identificationNumber: person.identificationNumber,
                idCardIssuedBy: person.idCardIssuedBy,
                idCardIssuedAt: person.idCardIssuedAt ? dayjs(person.idCardIssuedAt) : null,
                idCardValidity: person.idCardValidity ? dayjs(person.idCardValidity) : null,
                placeOfBirth: person.placeOfBirth,
                nationality: person.nationality,
                job: person.job,
                maritalStatus: person.maritalStatus ? person.maritalStatus : "",
                gender: person.gender,
                fatherName: person.fatherName,
                motherName: person.motherName,
            });
        }
    }, []);

    const cardTypeOpt = cardTypeMap();
    const genderOpt = genderMap();
    const maritalStatusOpt = maritalStatusMap();

    return (
        <SimpleBox mt={6} fullWidth={true}>
            <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>

                <Box textAlign="center"><Title label={"Dados de Sócio"} textAlign="center" /></Box>

                <ShadowBox title="Identificação" titleVariant="h3" mt={4}>
                    <SimpleBox flexDirection="column" gap={4}>
                        <TextFormInput id="fullName" label="Nome Completo" formik={formik} />
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="shortName" label="Nome Curto" formik={formik} />
                            <SelectFormInput id="idCardType" label="Tipo de Documento" formik={formik} options={cardTypeOpt} />
                            <TextFormInput id="identificationNumber" label="Nº Documento Identificação" formik={formik} />
                        </SimpleBox>
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormDate id="idCardValidity" label="Validade do Cartão" formik={formik} />
                            <TextFormDate id="idCardIssuedAt" label="Cartão Emitido Em" formik={formik} />
                            <TextFormInput id="idCardIssuedBy" label="Cartão Emitido Por" formik={formik} />
                        </SimpleBox>
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="fiscalNumber" label="NIF" formik={formik} />
                            <TextFormDate id="birthDate" label="Data de Nascimento" formik={formik} />
                            <SelectFormInput id="gender" label="Género" formik={formik} options={genderOpt} />
                        </SimpleBox>
                    </SimpleBox>
                </ShadowBox>

                <ShadowBox title="Contacto" titleVariant="h3" mt={4}>
                    <SimpleBox flexDirection="column" gap={4}>
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="phoneNrMobilePrimary" label="Telemóvel Principal" formik={formik} />
                            <TextFormInput id="phoneNrMobileSecondary" label="Telemóvel Secundário" formik={formik} />
                        </SimpleBox>
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="address" label="Morada" formik={formik} />
                            <TextFormInput id="postCode" label="Código Postal" formik={formik} />
                            <TextFormInput id="town" label="Localidade" formik={formik} />
                        </SimpleBox>
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="email" label="Email" formik={formik} type="email" />
                            <TextFormInput id="paymentEmail" label="Email de Pagamento" formik={formik} type="email" />
                        </SimpleBox>
                    </SimpleBox>
                </ShadowBox>

                <ShadowBox title="Adicional" titleVariant="h3" mt={4}>
                    <SimpleBox flexDirection="column" gap={4}>
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="placeOfBirth" label="Naturalidade" formik={formik} />
                            <TextFormInput id="nationality" label="Nacionalidade" formik={formik} />
                            <SelectFormInput id="maritalStatus" label="Estado Civil" formik={formik} options={maritalStatusOpt} />
                        </SimpleBox>
                        <TextFormInput id="job" label="Profissão" formik={formik} />
                        <SimpleBox gap={4} fullWidth={true} flexWrap={isMobile ? "wrap" : null} alignItems="start">
                            <TextFormInput id="fatherName" label="Nome do Pai" formik={formik} />
                            <TextFormInput id="motherName" label="Nome da Mãe" formik={formik} />
                        </SimpleBox>
                    </SimpleBox>
                </ShadowBox>

                {
                    paramTopic === "EXTERNAL" ? (
                        <SimpleBox mt={4} justifyContent="center">
                            <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey} onChange={setRecaptcha} onExpired={() => setRecaptcha(null)} />
                        </SimpleBox>
                    ) : null
                }

                <SimpleBox mt={4} mb={4}>
                    <BackButton />
                    <SendButton />
                </SimpleBox>
            </form>
            <DialogSubmit
                open={open}
                title="Dados de Sócio"
                message="Tens a certeza que pretendes submeter os dados?"
                actionMethod={handleSumbit}
            />
            <DialogSeasonConfirmation
                open={openExternal}
                season={null}
                actionMethod={handleSumbit} />
        </SimpleBox>
    );
};

export default PersonForm;
